import PropTypes from "prop-types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  // addFeedback,
  // removeFeedback,
  addSurveyCaption,
  addSurveyRating,
  addSurveyReviewContent,
} from "actions/happiness-survey-actions";
import { pages /*eventTypes*/ } from "constants/happiness-survey-values";
import SurveyItemBanner from "../feedback/item-banner";
import SurveyItemHeader from "../feedback/feedback-header";
import InputContainer from "../feedback/inputs/input-container";
// import ButtonContainer from "../feedback/inputs/button-container";
import FeedbackFooter from "../feedback/feedback-footer";
import HappinessSurveyRating from "./review-rating";
import ReviewPhotoUploader from "./review-photo-uploader";
import FitProfileSummary from "./fit-profile/fit-profile-summary";
import FitProfile from "../review_feedback/fit-profile/fit-profile";
import { updateProfile, fetchProfile } from "actions/user-actions";
import {
  parseUserProfileToFitProfile,
  parseFitProfileForSubmission,
} from "components/source/happiness_survey/hs_reviews_combined/fit-profile/fit-profile-helpers";

const propTypes = {
  currentSurveyIndex: PropTypes.number.isRequired,
  closeFeedbackScreen: PropTypes.func,
};

const ReviewModal = ({ currentSurveyIndex, closeFeedbackScreen = () => {} }) => {
  const currentSurvey = useSelector(state => state.happinessSurvey.itemSurveys[currentSurveyIndex]);
  if (!currentSurvey) return null;
  const userProfile = useSelector(state => state.userData?.userProfile) || {};

  const [headline, setHeadline] = React.useState(currentSurvey.caption || "");

  const [reviewContent, setReviewContent] = React.useState(currentSurvey.reviewContent || "");
  const dispatch = useDispatch();
  const toUi = r => (r >= 2 && r % 2 === 0 ? r / 2 : r || 0);
  const [rating, setRating] = React.useState(toUi(currentSurvey.rating));
  const [showEditFitProfile, setShowEditFitProfile] = React.useState(false);
  const [fitProfile, setFitProfile] = React.useState(userProfile || {});
  const [hasFitProfileError, setHasFitProfileError] = React.useState(false);

  React.useEffect(() => {
    const el = document.getElementById("hs-review-modal-root");
    el?.scrollTo?.(0, 0);
    window.scrollTo?.(0, 0);
  }, []);

  /*The commented code is ready in the frontend for the event types, but not yet in the backend. 
  Jira Ticket: https://renttherunway.jira.com/browse/RSV-1760 */

  // const optionsLabelProps = {
  //   headerText: "Where did you where this Item?",
  //   subHeaderText: "Select all that apply",
  // };

  const photoLabelProps = {
    headerText: "Let's see the look!",
    subHeaderText: "Photos help others discover what will fit them best.",
  };

  // const selectedButtons = React.useMemo(() => {
  //   const selected = new Set();
  //   currentSurvey.surveyFeedbacks?.forEach(fbEntry => {
  //     if (fbEntry.feedback in eventTypes) {
  //       selected.add(fbEntry.feedback);
  //     }
  //   });
  //   return selected;
  // }, [currentSurvey.surveyFeedbacks]);

  const handleSave = () => {
    if (hasFitProfileError) return;

    const parsedUserProfile = parseUserProfileToFitProfile(userProfile);
    const parsedFitProfile = parseUserProfileToFitProfile(fitProfile);
    const hasChanged = JSON.stringify(parsedUserProfile) !== JSON.stringify(parsedFitProfile);
    const saveSurvey = () => {
      if (headline) dispatch(addSurveyCaption(headline, currentSurvey.bookingId));
      if (reviewContent) dispatch(addSurveyReviewContent(reviewContent, currentSurvey.bookingId));
      if (rating) dispatch(addSurveyRating(rating * 2, currentSurvey.bookingId));
      closeFeedbackScreen();
    };

    if (hasChanged) {
      const onSuccess = () => {
        dispatch(fetchProfile());
        saveSurvey();
      };

      const onFail = err => {
        console.error("Fit profile update failed", err);
      };

      dispatch(updateProfile(parseFitProfileForSubmission(fitProfile), onSuccess, onFail));
    } else {
      saveSurvey();
    }
  };

  // const handleClickSelectionButton = data => {
  //   if (!selectedButtons.has(data)) {
  //     dispatch(addFeedback(data, currentSurvey.bookingId));
  //   } else {
  //     dispatch(removeFeedback(data, currentSurvey.bookingId));
  //   }
  // };

  const headlineCommentLabelProps = {
    headerText: "What should others know?",
    ctaElement: (
      <button
        onClick={() => {
          setHeadline("");
          setReviewContent("");
          dispatch(addSurveyCaption("", currentSurvey.bookingId));
          dispatch(addSurveyReviewContent("", currentSurvey.bookingId));
        }}
        className="text-sm font-medium text-onyx underline font-sans">
        Clear All
      </button>
    ),
  };

  return (
    <div id="hs-review-modal-root" className="flex flex-col h-full overflow-auto" data-test-id="hs-review-modal">
      <SurveyItemHeader
        title="Leave a Review"
        subtitle="Share your thoughts with the RTR community!"
        closeFeedbackScreen={closeFeedbackScreen}
      />

      <div className="flex flex-col px-4 grow">
        <SurveyItemBanner currentSurvey={currentSurvey} />
        <div className="mt-6 grow">
          <InputContainer
            labelProps={{
              headerText: "What did you think?",
              ctaElement: (
                <button
                  onClick={() => {
                    setRating(0);
                    dispatch(addSurveyRating(0, currentSurvey.bookingId));
                  }}
                  className="text-sm font-medium text-onyx underline font-sans"
                  data-test-id="clear-rating">
                  Clear All
                </button>
              ),
            }}>
            <HappinessSurveyRating value={rating} onChange={setRating} />
          </InputContainer>
          <InputContainer labelProps={photoLabelProps}>
            <ReviewPhotoUploader bookingId={currentSurvey.bookingId} />
          </InputContainer>

          <InputContainer labelProps={headlineCommentLabelProps}>
            <input
              type="text"
              maxLength="100"
              value={headline}
              placeholder="Headline"
              onChange={e => setHeadline(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-onyx"
            />
            <textarea
              maxLength="2000"
              value={reviewContent}
              placeholder="Comments"
              onChange={e => setReviewContent(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md text-md resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </InputContainer>
          {/* <InputContainer labelProps={optionsLabelProps}>
            <ButtonContainer
              selectedButtons={selectedButtons}
              buttons={eventTypes}
              hasFullWidthButtons={false}
              onClickSelectionButton={handleClickSelectionButton}
            />
          </InputContainer> */}
          <InputContainer
            labelProps={{
              headerText: "Your Fit Profile",
              subHeaderText: "Included on your review to help others find the right fit.",
              ctaElement: (
                <button
                  onClick={() => setShowEditFitProfile(prev => !prev)}
                  className="text-sm font-medium text-onyx underline font-sans">
                  {showEditFitProfile ? "Cancel" : "Edit"}
                </button>
              ),
            }}>
            {showEditFitProfile ? (
              <FitProfile
                fitProfile={fitProfile}
                setFitProfile={setFitProfile}
                setHasFitProfileError={setHasFitProfileError}
                showEditForm={true}
                hideHeader={true}
                isInReviewModal={true}
              />
            ) : (
              <FitProfileSummary userProfile={fitProfile} />
            )}
          </InputContainer>
        </div>
      </div>

      <FeedbackFooter ctaCopy="Submit Review" onClick={handleSave} />
    </div>
  );
};

ReviewModal.pageName = pages.REVIEW_MODAL;
ReviewModal.propTypes = propTypes;

export default ReviewModal;
